import React, { useMemo, useState } from 'react';
import { Alert, Box, Link, Modal, Stack, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormikErrors, useFormik } from 'formik';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import { CommonModalActionsBox, CommonModalContentBox, CommonModalWrapperBox } from '../../../../common/styles/Common.styles';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { FINANCE_SETTINGS } from '../../../../common/constants/routes.constants';
import { ActionMode, BillingSetupSubTab, GeneralStatusEnum } from '../../../../common/enums/common.enum';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import {
  calculateLineTotals,
  DEFAULT_CURRENCY,
  formatMoney,
  parseMoneyInput,
  toMoneyInputValue
} from '../../../../utilities/helper/money.helper';
import { BillingItemTypeEnum } from '../../../../common/enums/invoicing.enum';
import { BillingItemDto } from '../../../../common/interfaces/invoicing.interface';
import { createBillingItem, overrideBillingItem, updateBillingItem } from '../../../../api/invoicing.api';
import { FieldFrame, FormTextField, messageId } from '../Form/FormField';
import SegmentedControl from '../../../../components/SegmentedControl';
import SystemValue from '../Form/SystemValue';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ErrorSummary, { SummaryItem } from '../Form/ErrorSummary';
import { readServerErrors } from '../Form/serverErrors';
import { collectErrors, parseDecimal } from '../../validation/rules';
import {
  BILLING_ITEM_FIELDS,
  BillingItemFormValues,
  billingItemSchema,
  toItemFormValues,
  VAT_RATES
} from '../../validation/billingItem.schema';

interface BillingItemModalProps {
  mode: ActionMode;
  kindergartenId: string;
  /** Whether the kindergarten is in the VAT system; without it an item carries no VAT. */
  vatApplicable: boolean;
  item?: BillingItemDto | null;
  onClose: () => void;
  onSaved: () => void;
}

type Field = keyof BillingItemFormValues;

const fieldId = (field: Field) => `bi-${field}`;

const LABELS: Record<Field, string> = {
  name: 'billing.items.name',
  pdfLabel: 'billing.lines.invoiceLabel',
  type: 'billing.items.calculation',
  unitPriceNet: 'billing.items.netPrice',
  vatRate: 'billing.items.vatRate',
  unit: 'billing.items.unit',
  defaultQuantity: 'billing.items.quantity',
  status: 'common.status'
};

const vatLabel = (t: TFunction, rate: number) => (rate === 0 ? t('billing.items.noVat') : `${rate} %`);

/**
 * One catalogue item: what it is called inside, what the parent reads, how it is counted
 * and what it costs.
 *
 * The printed name follows the internal one until it is typed in - most items are called
 * the same in both places, and a second field to fill in the same is how they drift apart.
 */
const BillingItemModal: React.FC<BillingItemModalProps> = ({ mode, kindergartenId, vatApplicable, item, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<Partial<Record<Field, string>>>({});
  const [serverOther, setServerOther] = useState<string[]>([]);
  // a platform item is maintained centrally: this kindergarten adjusts it through an
  // override, and only the fields it actually changes are stored
  const isPlatform = !!item?.isPlatformItem;
  const defaults = item?.platformDefaults ?? null;
  const overridden = (field: string) => !!item?.overriddenFields.includes(field);

  const schema = useMemo(() => billingItemSchema(t, { vatApplicable }), [t, vatApplicable]);
  const formik = useFormik<BillingItemFormValues>({
    initialValues: toItemFormValues(item, vatApplicable),
    validate: values => collectErrors(schema, values) as FormikErrors<BillingItemFormValues>,
    onSubmit: () => undefined
  });
  const { values, errors, touched, setFieldValue, setTouched, validateForm, resetForm } = formik;
  const isAttendance = values.type === BillingItemTypeEnum.ATTENDANCE_BASED;

  const shown = (field: Field) => attempted || !!touched[field];
  const errorOf = (field: Field) => (shown(field) ? serverErrors[field] || (errors[field] as string | undefined) : undefined);
  const set = (field: Field, value: unknown) => {
    setFieldValue(field, value);
    if (serverErrors[field]) setServerErrors(prev => ({ ...prev, [field]: undefined }));
  };
  const text = (field: Field) => ({
    id: fieldId(field),
    name: field,
    label: t(LABELS[field]),
    value: values[field] as string,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => set(field, event.target.value),
    onBlur: formik.handleBlur,
    error: errorOf(field)
  });

  /** Sends null for the field, which clears the override and restores the platform value. */
  const resetField = async (field: 'pdfLabel' | 'unitPriceNet' | 'vatRate' | 'unit') => {
    if (!item) return;
    try {
      const result = await overrideBillingItem(kindergartenId, item.id, { [field]: null });
      const fresh = result.results;
      if (fresh) {
        setFieldValue('pdfLabel', fresh.pdfLabel);
        setFieldValue('unitPriceNet', toMoneyInputValue(fresh.unitPriceNet));
        setFieldValue('vatRate', fresh.vatRate);
        setFieldValue('unit', fresh.unit ?? '');
      }
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.items.resetDone'));
      onSaved();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('billing.items.resetFailed'));
    }
  };

  const resetLink = (field: 'pdfLabel' | 'unitPriceNet' | 'vatRate' | 'unit', shownValue: string) =>
    isPlatform && overridden(field) ? (
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
        {t('billing.items.platformDefault', { value: shownValue })}{' '}
        <Link component="button" type="button" onClick={() => resetField(field)} sx={{ fontSize: 12 }}>
          {t('billing.items.reset')}
        </Link>
      </Typography>
    ) : null;

  const handleSave = async (keepOpen: boolean): Promise<void> => {
    setAttempted(true);
    setTouched(Object.fromEntries(BILLING_ITEM_FIELDS.map(field => [field, true])), false);
    const found = await validateForm();
    const failing = BILLING_ITEM_FIELDS.filter(field => found[field] || serverErrors[field]);
    if (failing.length) {
      document.getElementById(fieldId(failing[0]))?.focus();
      return;
    }

    const name = values.name.trim();
    // empty means "the same as the name", which is what is then stored
    const pdfLabel = values.pdfLabel.trim() || name;
    const unitPriceNet = parseMoneyInput(values.unitPriceNet) as number;
    const vatRate = vatApplicable ? (values.vatRate ?? 0) : 0;
    setServerOther([]);
    try {
      if (isPlatform && item) {
        // only the overridable fields go up; the internal name and calculation type are
        // the same across the platform and are not editable here
        await overrideBillingItem(kindergartenId, item.id, {
          pdfLabel,
          unitPriceNet,
          vatRate: vatApplicable ? vatRate : undefined,
          unit: values.unit.trim() || null,
          status: values.status
        });
        createNotification(MESSAGE_TYPE.SUCCESS, t('billing.items.adjusted'));
        onSaved();
        onClose();
        return;
      }

      const dto = {
        name,
        pdfLabel,
        type: values.type,
        unitPriceNet,
        vatRate,
        unit: values.unit.trim() || undefined,
        // an attendance-based item takes its quantity from recorded attendance
        defaultQuantity: isAttendance ? 1 : (parseDecimal(values.defaultQuantity) ?? 1),
        status: values.status
      };
      if (mode === ActionMode.CREATE) await createBillingItem(kindergartenId, dto);
      else if (item) await updateBillingItem(kindergartenId, item.id, dto);
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.items.saved'));
      onSaved();
      if (keepOpen) {
        resetForm({ values: { ...toItemFormValues(null, vatApplicable), type: values.type, vatRate: values.vatRate } });
        setAttempted(false);
        document.getElementById(fieldId('name'))?.focus();
      } else {
        onClose();
      }
    } catch (error: any) {
      const read = readServerErrors(error, BILLING_ITEM_FIELDS);
      if (!read) {
        createNotification(MESSAGE_TYPE.ERROR, t('billing.items.saveFailed'));
        return;
      }
      setServerErrors(read.fields as Partial<Record<Field, string>>);
      setServerOther(read.other);
    }
  };

  const summary: SummaryItem[] = attempted
    ? [
        ...BILLING_ITEM_FIELDS.filter(field => errorOf(field)).map(field => ({
          id: fieldId(field),
          label: t(LABELS[field]),
          message: errorOf(field) as string
        })),
        ...serverOther.map(message => ({ message }))
      ]
    : [];

  const net = parseMoneyInput(values.unitPriceNet);
  const gross = typeof net === 'number' && values.vatRate !== null ? calculateLineTotals(net, 1, values.vatRate).gross : null;
  const unitShown = values.unit.trim() || t('billing.items.perUnit');

  return (
    <Modal open onClose={onClose}>
      <CommonModalWrapperBox sx={{ width: 640, maxHeight: '90vh', overflowY: 'auto' }}>
        <CommonModalContentBox>
          <Typography variant="h2" sx={{ fontSize: 22, mb: 2 }}>
            {mode === ActionMode.CREATE ? t('billing.items.create') : isPlatform ? t('billing.items.adjust') : t('billing.items.edit')}
          </Typography>

          <ErrorSummary items={summary} radius="10px" />

          {isPlatform && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {t('billing.items.platformItemNotice')}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 2.5 }}>
            <FormTextField {...text('name')} required locked={isPlatform} hint={t('billing.items.internalNameHelp')} />
            <Box>
              <FormTextField
                {...text('pdfLabel')}
                placeholder={values.name.trim() || t('billing.items.labelSameAsName')}
                hint={t('billing.items.labelFollowsName')}
              />
              {resetLink('pdfLabel', defaults?.pdfLabel ?? '')}
            </Box>

            <FieldFrame id={fieldId('type')} labelId={`${fieldId('type')}-label`} label={t(LABELS.type)} wide disabled={isPlatform}>
              <SegmentedControl
                id={fieldId('type')}
                labelledBy={`${fieldId('type')}-label`}
                value={values.type}
                onChange={value => set('type', value)}
                locked={isPlatform}
                width={360}
                options={[
                  { value: BillingItemTypeEnum.FIXED_QUANTITY, label: t('billing.items.monthly') },
                  { value: BillingItemTypeEnum.ATTENDANCE_BASED, label: t('billing.items.perAttendanceDay') }
                ]}
              />
            </FieldFrame>

            <Box>
              <FormTextField
                {...text('unitPriceNet')}
                required
                suffix={DEFAULT_CURRENCY}
                hint={t('billing.items.priceHint')}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              {resetLink('unitPriceNet', formatMoney(defaults?.unitPriceNet))}
            </Box>
            <Box>
              <FormTextField {...text('unit')} hint={t('billing.items.unitExampleAny')} />
              {resetLink('unit', defaults?.unit ?? '—')}
            </Box>

            {vatApplicable ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <FieldFrame
                  id={fieldId('vatRate')}
                  labelId={`${fieldId('vatRate')}-label`}
                  label={t(LABELS.vatRate)}
                  required
                  error={errorOf('vatRate')}
                  hint={t('billing.items.vatHint')}
                >
                  <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
                    <SegmentedControl
                      id={fieldId('vatRate')}
                      labelledBy={`${fieldId('vatRate')}-label`}
                      describedBy={messageId(fieldId('vatRate'))}
                      invalid={!!errorOf('vatRate')}
                      value={values.vatRate}
                      onChange={value => set('vatRate', value)}
                      width={300}
                      options={VAT_RATES.map(rate => ({ value: rate, label: vatLabel(t, rate) }))}
                    />
                    {gross !== null && (
                      <Typography sx={{ fontSize: 14, color: 'text.secondary' }} aria-live="polite">
                        {t('billing.items.withVat', { amount: formatMoney(gross, { withCurrency: true }), unit: unitShown })}
                      </Typography>
                    )}
                  </Stack>
                </FieldFrame>
                {resetLink('vatRate', defaults ? vatLabel(t, defaults.vatRate) : '')}
              </Box>
            ) : (
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  bgcolor: 'surface.tint',
                  borderRadius: '10px',
                  p: '12px 16px',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start'
                }}
              >
                <InfoOutlinedIcon aria-hidden sx={{ fontSize: 18, color: 'info.main', mt: '1px' }} />
                <Typography sx={{ fontSize: 13 }}>
                  {t('billing.items.notInVat')}{' '}
                  <Link
                    component={RouterLink}
                    to={`${FINANCE_SETTINGS}?kindergartenId=${kindergartenId}&tab=${BillingSetupSubTab.Settings}#s-vat`}
                    onClick={onClose}
                  >
                    {t('billing.items.notInVatLink')}
                  </Link>
                </Typography>
              </Box>
            )}

            {isAttendance ? (
              // the quantity is the month's attendance: there is nothing to type, so no field
              <SystemValue
                id={fieldId('defaultQuantity')}
                label={t(LABELS.defaultQuantity)}
                icon={<EventOutlinedIcon />}
                hint={t('billing.items.attendanceQuantityHint')}
              >
                {t('billing.items.attendanceQuantity')}
              </SystemValue>
            ) : (
              <FormTextField
                {...text('defaultQuantity')}
                hint={t('billing.items.prefilledOnLine')}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
            )}
            <FieldFrame id={fieldId('status')} labelId={`${fieldId('status')}-label`} label={t(LABELS.status)}>
              <SegmentedControl
                id={fieldId('status')}
                labelledBy={`${fieldId('status')}-label`}
                value={values.status}
                onChange={value => set('status', value)}
                options={[
                  { value: GeneralStatusEnum.ACTIVE, label: t('enums.generalStatus.Active') },
                  { value: GeneralStatusEnum.INACTIVE, label: t('enums.generalStatus.Inactive') }
                ]}
              />
            </FieldFrame>
          </Box>
        </CommonModalContentBox>

        <CommonModalActionsBox>
          {mode === ActionMode.CREATE && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Secondary}
              variant="outlined"
              onAsyncAction={() => handleSave(true)}
              label={t('common.saveAndCreateAnother')}
            />
          )}
          <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" onClick={onClose} label={t('common.cancel')} />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            onAsyncAction={() => handleSave(false)}
            label={t('common.save')}
          />
        </CommonModalActionsBox>
      </CommonModalWrapperBox>
    </Modal>
  );
};

export default BillingItemModal;
